import { isEmpty, isNil } from 'lodash'

import { normalizeFieldKey } from './index'
import { canDependOn, getLinksForTarget, reachesField } from './fieldLinks.helpers'

// A field's answer can be written into another field's prompt as a token. The
// service publishes each upstream field under several spellings at once, and
// this module mirrors that set exactly (see `fieldAliases`): the field's name
// verbatim, its name lowercased, and the legacy slug
// `field_name.lower().replace(" ", "_")`.
//
// `${Field Name}` is the form to write and the only one we generate. A bare
// `$name` has no terminator, so the service's identifier pattern
// (`[_a-z0-9][_a-z0-9]*`) stops at the first character outside it: `$Follow-Up`
// substitutes `follow` and leaves `-Up` in the prompt as literal text. Braces
// supply the terminator, so any name at all can be referenced — which is why
// punctuated fields are offered by the picker now and used to be hidden from it.
//
// The edge IS the mechanism, not documentation of it: substitution walks the
// target's incoming edges and nothing scans the prompt text, so a token with no
// edge behind it reaches the model as literal characters. That is why writing a
// token has to create an edge (see getTokenLinks).
//
// Everything here is pure over (promptFields, links) so the token rules are
// testable without rendering.

// Braced first, so `${Call Type}` is read whole rather than as a bare `$` token
// that stops at the brace. Anything but `{`/`}` is legal inside braces, matching
// the service's `braceidpattern`. The bare arm stays case-insensitive to match
// the service's regex, which is where the trap is: it MATCHES any case but LOOKS
// UP the exact key, so `$Call_Type` is found by the regex, resolves to nothing,
// and is left in the prompt verbatim.
const TOKEN_PATTERN = /\$(?:\{([^{}]*)\}|([_a-zA-Z0-9]+))/g

// Variables the prompt already provides. They are not field references and must
// never be flagged as broken ones.
const RESERVED_TOKENS = new Set([
  'transcript',
  'bit_json',
  'message_transcript',
  'description',
  'taxonomy',
  'agentname',
  'agentfirst',
  'agentlast',
  'callduration',
  'callstart',
  'callend',
])

// Whether a link exists only to carry a value. Conditions read as chips and draw
// the rail; a data link has no condition to state, so the two are told apart
// wherever the editor renders one.
export const isDataLink = (link) => isNil(link?.operator)

// The legacy variable name a field's answer also arrives under — what prompts
// written before braces existed contain. Kept for reading existing text, never
// generated: `toFieldReference` is what we write.
export const toFieldToken = (fieldName) => (fieldName ?? '').trim().toLowerCase().replace(/ /g, '_')

// The spelling to write, and the only one this editor inserts. Always braced and
// always the field's own name, so what the author picked is what they read back,
// and so a name the legacy slug can't express ("Co-Borrower's Name") still works.
export const toFieldReference = (fieldName, subKey = null) => {
  const name = (fieldName ?? '').trim()
  return isNil(subKey) ? `\${${name}}` : `\${${name}.${subKey}}`
}

// Every spelling that resolves to this field, mirroring what the service
// publishes: the name, the name lowercased (references are case-insensitive),
// and the legacy slug. Order matters only for readability — lookups are exact.
export const fieldAliases = (fieldName) => {
  const name = (fieldName ?? '').trim()
  return Array.from(new Set([name, name.toLowerCase(), toFieldToken(name)])).filter(
    (alias) => !isEmpty(alias)
  )
}

// Every token in a description, in the order written, with where it sits so a
// caller can point at it. `braced` is carried through because the two forms fail
// differently — a bare token can't hold punctuation, a braced one can — so the
// advice offered about a broken token depends on which was written.
export const findTokens = (text) => {
  const found = []
  const matches = (text ?? '').matchAll(TOKEN_PATTERN)
  Array.from(matches).forEach((match) => {
    const braced = !isNil(match[1])
    found.push({
      raw: match[0],
      name: braced ? match[1] : match[2],
      braced,
      index: match.index,
    })
  })
  return found
}

// The fields of a flow indexed by every spelling that would reach them, so a
// lookup is one exact hit rather than a scan. Null-prototype: a field named
// "Constructor" or "toString" produces a key that exists on Object.prototype,
// which would make the lookup return a function and the guard below see it as
// already taken.
//
// First claim wins, and identity aliases are claimed before any legacy slug —
// the same two-tier order the service uses, so a field named `call_type` keeps
// `$call_type` even when a "Call Type" field is also in the flow. Doing it in one
// pass would let whichever field came first take the other's name outright.
export const buildTokenIndex = (promptFields = []) => {
  const index = Object.create(null)
  const claim = (alias, field) => {
    if (!isEmpty(alias) && isNil(index[alias])) {
      index[alias] = field
    }
  }
  promptFields.forEach((field) => {
    const name = (field.key ?? '').trim()
    claim(name, field)
    claim(name.toLowerCase(), field)
  })
  promptFields.forEach((field) => claim(toFieldToken(field.key), field))
  return index
}

// Two fields whose names differ only by spacing or case collapse to one token —
// "Call Type" and "call_type" are both `$call_type`. The service builds a dict,
// so one silently wins and the prompt gets a value from a field the author
// didn't name.
export const findTokenCollisions = (promptFields = []) => {
  const byToken = Object.create(null)
  promptFields.forEach((field) => {
    const token = toFieldToken(field.key)
    if (isEmpty(token)) {
      return
    }
    byToken[token] = [...(byToken[token] ?? []), field.key]
  })
  return Object.entries(byToken)
    .filter(([, names]) => names.length > 1)
    .map(([token, names]) => ({ token, names }))
}

// What a reference points at: the field, whether it was spelled exactly (the
// service looks its keys up by string even though its regex is case-insensitive,
// so a merely case-equivalent spelling finds nothing), and the sub-key if one
// was addressed.
//
// A reference is a field name optionally followed by a structured-response
// sub-key — `${Call Reason}` or `${Call Reason.answer}` — and a field name may
// itself contain a period, so the split point isn't decidable from the text
// alone. The longest prefix that names a field wins, which is the same order the
// service claims spellings in: an explicit name outranks a sub-key that happens
// to spell the same thing. Without this, `${Call Reason.answer}` matched no field
// and so minted no edge, and since the edge IS the mechanism, the answer never
// reached the prompt at all.
// The candidate field names inside a reference, longest first: the whole thing,
// then each dot-prefix. `${Call Reason.answer}` yields "Call Reason.answer" then
// "Call Reason". Longest-first is what makes a period-bearing field name beat a
// sub-key that spells the same thing, matching the service's claim order.
const referenceCandidates = (name = '') => {
  const candidates = [{ base: name, subKey: null }]
  for (let dot = name.lastIndexOf('.'); dot > 0; dot = name.lastIndexOf('.', dot - 1)) {
    candidates.push({ base: name.slice(0, dot), subKey: name.slice(dot + 1) })
  }
  return candidates
}

const resolveReference = (index, name = '') => {
  for (const { base, subKey } of referenceCandidates(name)) {
    const exact = index[base]
    if (!isNil(exact)) {
      return { field: exact, exact: true, subKey }
    }
    const loose = index[base.toLowerCase()]
    if (!isNil(loose)) {
      return { field: loose, exact: false, subKey }
    }
  }
  return null
}

export const TOKEN_PROBLEM = {
  // Resolved when the flow loaded and doesn't now — its field was renamed or
  // removed in this session.
  stale: 'stale',
  // A field answers to it, but not under the case it was written in — the
  // service matches any case and then looks up the exact key, so this one
  // resolves to nothing.
  miscased: 'miscased',
  // The field exists and runs later, so its answer can't be waited for.
  wrongOrder: 'wrongOrder',
  // Depending on it would make a cycle, which the service rejects outright.
  cycle: 'cycle',
}

// The edges the descriptions imply, judged on name alone. Cycle detection needs
// these: two fields can reference each other with no edge between them yet, so
// checking only the saved links would pass a pair that the service then
// rejects as a cycle.
const candidateTokenLinks = (promptFields = []) => {
  const index = buildTokenIndex(promptFields)
  const candidates = []
  promptFields.forEach((field) => {
    const seen = new Set()
    findTokens(field.value).forEach(({ name }) => {
      const lowered = name.toLowerCase()
      const hit = resolveReference(index, name)
      if (RESERVED_TOKENS.has(lowered) || seen.has(lowered) || isNil(hit) || !hit.exact) {
        return
      }
      // Judged on the field reached rather than on the legacy slug: a braced
      // `${Call Type}` in the field "Call Type" never equalled `call_type` as
      // text, so a text comparison let a self-edge into the candidate set that
      // cycle detection then read as a real dependency.
      if (normalizeFieldKey(hit.field.key) === normalizeFieldKey(field.key)) {
        return
      }
      seen.add(lowered)
      candidates.push({ source: hit.field.key, target: field.key, operator: null })
    })
  })
  return candidates
}

// What is wrong with the tokens in one field's description, if anything.
//
// A token naming no field is NOT a problem. Descriptions are prose and full of
// dollar signs — "$1000", "get $X dollars" — and treating those as broken
// references made the editor unusable on existing content. An unresolvable
// token also costs nothing: it mints no edge, and the service leaves it in the
// prompt verbatim.
//
// What the reader can't see for themselves is a reference that USED to work.
// Renaming or deleting a field silently breaks every description that read it,
// so those are flagged against the names the flow loaded with. Everything else
// relies on the Uses pill: a token that links shows one, so a token that
// doesn't is visibly doing nothing.
//
// @param {string[]} knownFieldNames the flow's field names as loaded, before
//   this session's edits
export const findTokenProblems = (field, promptFields = [], links = [], knownFieldNames = []) => {
  const index = buildTokenIndex(promptFields)
  // Every spelling the flow's old names answered to, lowercased so a rename is
  // caught whichever form the author wrote it in.
  const knownAliases = new Set(
    knownFieldNames.flatMap((fieldName) =>
      fieldAliases(fieldName).map((alias) => alias.toLowerCase())
    )
  )
  // A reference names a field, so a rename breaks `${Call Reason.answer}` exactly
  // as it breaks `${Call Reason}` — checking the whole spelling alone would let
  // the dotted one read as prose and the save proceed without its edge.
  const namedAKnownField = (name) =>
    referenceCandidates(name).some(({ base }) => knownAliases.has(base.toLowerCase()))
  const problems = []
  const seen = new Set()
  const candidates = candidateTokenLinks(promptFields)

  findTokens(field.value).forEach(({ raw, name, braced }) => {
    const lowered = name.toLowerCase()
    if (RESERVED_TOKENS.has(lowered) || seen.has(raw)) {
      return
    }
    // Resolved loosely as well, so a token that named a real field under the
    // wrong case is told apart from prose and gets the miscased advice.
    const hit = resolveReference(index, name)
    const source = hit?.field
    if (isNil(source)) {
      // Prose unless the flow used to have a field by that name.
      if (namedAKnownField(name)) {
        seen.add(raw)
        problems.push({ raw, name, braced, problem: TOKEN_PROBLEM.stale })
      }
      return
    }
    seen.add(raw)
    // A field referring to itself is a typo, not a dependency — and the service
    // would reject the self-edge as a cycle. Judged on the field it resolved to
    // rather than on spelling: `${Call Type}` and `$call_type` are the same
    // reference, and neither matches the other as text.
    if (normalizeFieldKey(source.key) === normalizeFieldKey(field.key)) {
      problems.push({ raw, name, braced, problem: TOKEN_PROBLEM.cycle })
      return
    }
    if (!hit.exact) {
      problems.push({
        raw,
        name,
        braced,
        problem: TOKEN_PROBLEM.miscased,
        source,
        subKey: hit.subKey,
      })
      return
    }
    if (!canDependOn(field, source)) {
      problems.push({ raw, name, braced, problem: TOKEN_PROBLEM.wrongOrder, source })
      return
    }
    // Against the edges this text would mint as well as the saved ones, minus
    // the one being judged — otherwise every token trivially "reaches" itself.
    const others = [...links, ...candidates].filter(
      (link) =>
        !(
          normalizeFieldKey(link.source) === normalizeFieldKey(source.key) &&
          normalizeFieldKey(link.target) === normalizeFieldKey(field.key)
        )
    )
    if (reachesField(others, field.key, source.key)) {
      problems.push({ raw, name, braced, problem: TOKEN_PROBLEM.cycle, source })
    }
  })

  return problems
}

// What to tell the author, naming the token they wrote so they can find it in
// the text. Each reason is a different fix, so none of them share wording.
export const describeTokenProblem = ({ raw, problem, source, subKey = null }) => {
  switch (problem) {
    case TOKEN_PROBLEM.miscased:
      return `${raw} won't resolve - write it as ${toFieldReference(source.key, subKey)}`
    case TOKEN_PROBLEM.wrongOrder:
      return `${raw} is processed later, a field shown to the agent can only read others shown to the agent`
    case TOKEN_PROBLEM.cycle:
      return `${raw} already depends on this field, so reading it back would make a loop`
    default:
      return `${raw} named a field this playbook no longer has - it was renamed or removed`
  }
}

// One line for the whole description, since the field shows a single message.
export const describeTokenProblems = (problems = []) =>
  problems.map(describeTokenProblem).join('. ')

// Every field holding a broken token, so a save can name them rather than
// failing on the wire.
export const findFieldsWithBrokenTokens = (promptFields = [], links = [], knownFieldNames = []) =>
  promptFields
    .map((field) => ({
      field,
      problems: findTokenProblems(field, promptFields, links, knownFieldNames),
    }))
    .filter(({ problems }) => !isEmpty(problems))

// Whether a description reads any field's answer at all — resolvable or not.
// Copying such a description into another playbook would carry a reference the
// receiving flow has no edge for, and quite possibly no such field either, so
// the copy is refused rather than silently landing dead text.
export const hasFieldReferences = (text) =>
  findTokens(text).some(({ name }) => !RESERVED_TOKENS.has(name.toLowerCase()))

// The fields whose answers a description pulls in — the resolvable tokens only.
export const getTokenSources = (field, promptFields = [], links = []) => {
  const index = buildTokenIndex(promptFields)
  const broken = new Set(findTokenProblems(field, promptFields, links).map(({ raw }) => raw))
  const sources = []
  const seen = new Set()

  findTokens(field.value).forEach(({ raw, name }) => {
    if (broken.has(raw) || RESERVED_TOKENS.has(name.toLowerCase())) {
      return
    }
    const source = resolveReference(index, name)?.field
    // Deduped by the field reached, not by the text: `${Call Reason}` and
    // `${Call Reason.answer}` are two spellings of one dependency, and listing
    // it twice would draw two "Uses" chips for a single edge.
    if (!isNil(source) && !seen.has(normalizeFieldKey(source.key))) {
      seen.add(normalizeFieldKey(source.key))
      sources.push(source)
    }
  })
  return sources
}

// The edges a flow's tokens require. An edge already linking the pair carries
// the value whatever its operator — the service strips the condition when it
// builds the prompt inputs — so a gated field reading its gate's answer needs
// nothing added. Only an unlinked pair mints an operator-less edge.
export const getTokenLinks = (promptFields = [], links = []) => {
  const added = []
  promptFields.forEach((field) => {
    getTokenSources(field, promptFields, links).forEach((source) => {
      const alreadyLinked = [...links, ...added].some(
        (link) =>
          normalizeFieldKey(link.source) === normalizeFieldKey(source.key) &&
          normalizeFieldKey(link.target) === normalizeFieldKey(field.key)
      )
      if (!alreadyLinked) {
        added.push({
          source: source.key,
          target: field.key,
          operator: null,
          compare_value: null,
        })
      }
    })
  })
  return added
}

// The links a save should carry. Conditions are authored and kept as they are;
// data edges are DERIVED from the descriptions, so they're recomputed rather
// than accumulated — appending to what was loaded would leave an edge behind
// when its `$token` is deleted, and keep injecting a value nothing asks for.
//
// A consequence worth knowing: a data edge created outside this editor, with no
// token behind it, is dropped on the next save. That follows from the text
// being the link — there is nothing else for the editor to read it from.
export const withTokenLinks = (promptFields = [], links = []) => {
  const conditions = links.filter((link) => !isDataLink(link))
  return [...conditions, ...getTokenLinks(promptFields, conditions)]
}

// A name the reference syntax can't express. `${...}` is delimited by braces, so
// a field called `Rate {USD}` has no spelling that reaches it — `${Rate {USD}}`
// doesn't parse, and TOKEN_PATTERN excludes braces by the same token. The service
// rejects these names at save now (BitFlowFieldInput), but a flow saved before
// that may still carry one, so the picker keeps filtering rather than offering a
// field that cannot work.
//
// This is the ONLY spelling test left. It used to exclude any name outside
// `[_a-z0-9]+`, which quietly hid every field with a hyphen or an apostrophe.
export const isReferenceableFieldName = (fieldName) => !/[{}]/.test(fieldName ?? '')

// The fields a description could reference from here, for the picker: named,
// not itself, legal to depend on, no cycle, and expressible as a reference.
export const getTokenCandidates = (field, promptFields = [], links = []) =>
  promptFields.filter(
    (candidate) =>
      !isEmpty((candidate.key ?? '').trim()) &&
      normalizeFieldKey(candidate.key) !== normalizeFieldKey(field.key) &&
      isReferenceableFieldName(candidate.key) &&
      canDependOn(field, candidate) &&
      !reachesField(links, field.key, candidate.key)
  )

// The token being typed at the caret, if any. Two shapes reach here: `${` with
// anything but a closing brace after it, and a bare `$` with token characters.
// The braced arm is tried first — inside braces a space is part of the name, so
// treating `${Call T` as a bare token would end the match at the brace and
// suggest nothing once the author typed a space.
//
// `end` runs past the caret to the end of what is being typed, so completing
// mid-token replaces the whole thing rather than splicing into it. For a braced
// token that runs through its closing brace, but only when one is already there
// on the same line. An unclosed `${` has no end to find: a space is part of the
// name, so reaching for the next brace would swallow the rest of the prose
// after the caret and the pick would delete it. Then only the word under the
// caret counts, as it does for a bare token. A `$` doesn't stop the match —
// isReferenceableFieldName bans only braces, so `${Amount ($)}` is a name.
//
// Neither side crosses a line. A name never spans one, so a `${` left unclosed
// on an earlier line isn't what the author is typing now, and completing it
// would take the newline with it.
export const findActiveToken = (text = '', caret = 0) => {
  const before = text.slice(0, caret)
  const after = text.slice(caret)

  const bracedMatch = before.match(/\$\{([^{}\n]*)$/)
  if (!isNil(bracedMatch)) {
    const trailing = after.match(/^[^{}\n]*\}/) ?? after.match(/^[_a-zA-Z0-9]*/)
    return {
      start: caret - bracedMatch[0].length,
      end: caret + (trailing?.[0].length ?? 0),
      query: bracedMatch[1],
    }
  }

  const match = before.match(/\$([_a-zA-Z0-9]*)$/)
  if (isNil(match)) {
    return null
  }
  const trailing = after.match(/^[_a-zA-Z0-9]*/)
  return {
    start: caret - match[0].length,
    end: caret + (trailing?.[0].length ?? 0),
    query: match[1],
  }
}

// Replace the token being typed with a whole one, and say where the caret should
// land — after the inserted token, not at the end of the text. Always the braced
// form, whichever form was being typed: it is the spelling that works for every
// name, so there is no reason to generate the one that works for some.
export const insertToken = (text = '', active, fieldName) => {
  const token = toFieldReference(fieldName)
  return {
    text: `${text.slice(0, active.start)}${token}${text.slice(active.end)}`,
    caret: active.start + token.length,
  }
}

// The fields a half-typed token could mean. Matched against every spelling the
// field answers to, so typing `date_of` finds "Date Of Service" and typing
// `co-b` finds "Co-Borrower's Name" — the display name and the legacy slug are
// both things an author might be part-way through. The field's own name is never
// among them (see getTokenCandidates).
//
// Fields only — never `${field.subKey}`. Sub-keys are a last resort for an admin
// chasing accuracy on a structured response, not a thing to put in front of
// everyone writing a prompt: which ones exist depends on what the model returned
// on a given call, so the picker can't honestly enumerate them, and offering
// them would make the common case (the field's answer) look like the unusual
// one. A dot in the query matches no alias, so typing one closes the list rather
// than opening a second level — which is the behaviour this relies on.
export const getTokenSuggestions = (field, promptFields = [], links = [], query = '') => {
  const lowered = query.trim().toLowerCase()
  return getTokenCandidates(field, promptFields, links).filter((candidate) =>
    fieldAliases(candidate.key).some((alias) => alias.toLowerCase().includes(lowered))
  )
}

export const getConditionsForTarget = (links, fieldKey) =>
  getLinksForTarget(links, fieldKey).filter((link) => !isDataLink(link))
